import { ExternalLink, MapPin, Navigation } from "lucide-react";

import { googleMapsUrl, mapEmbedUrl, wazeUrl, type PickupPoint } from "@/data/locations";
import { cn } from "@/lib/utils";

interface LocationMapProps {
  point: PickupPoint;
  className?: string;
  height?: string;
  showAddress?: boolean;
}

export function LocationMap({
  point,
  className,
  height = "h-48",
  showAddress = true,
}: LocationMapProps) {
  const gmaps = googleMapsUrl(point.lat, point.lng);
  const waze = wazeUrl(point.lat, point.lng);

  return (
    <div className={cn("overflow-hidden rounded-xl border border-gold/25 bg-card", className)}>
      <a
        href={gmaps}
        target="_blank"
        rel="noopener noreferrer"
        className={cn("relative block w-full", height)}
        aria-label={`Open ${point.address} in Google Maps`}
      >
        <iframe
          title={`Map of ${point.address}`}
          src={mapEmbedUrl(point.lat, point.lng)}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="pointer-events-none size-full grayscale-[0.35] contrast-[1.05]"
        />
        <span className="absolute inset-0 bg-ink/10" />
      </a>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-3">
        {showAddress ? (
          <p className="flex min-w-0 items-start gap-1.5 text-xs text-muted-foreground">
            <MapPin className="mt-0.5 size-3.5 shrink-0 text-gold" />
            <span className="truncate">{point.address}</span>
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <a
            href={gmaps}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-gold/40 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-gold transition-colors hover:bg-gold/10"
          >
            <ExternalLink className="size-3.5" /> Google Maps
          </a>
          <a
            href={waze}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:border-gold/40 hover:text-gold"
          >
            <Navigation className="size-3.5" /> Waze
          </a>
        </div>
      </div>
    </div>
  );
}
